<template>
  <!-- Native <dialog> + showModal() (the MModalDialog house pattern): the browser supplies
       dialog semantics, initial focus, a focus trap, and an inert background — a keyboard or
       screen-reader user can't land on the obscured page. Esc is blocked (@cancel.prevent):
       only takeover or reload resolves the gate, never dismissal. -->
  <dialog
    ref="dialogRef"
    class="w-full max-w-md border-none bg-transparent p-0"
    aria-labelledby="admin-session-gate-title"
    @cancel.prevent
  >
    <div class="card bg-base-100 shadow-xl">
      <div class="card-body">
        <h2 id="admin-session-gate-title" class="card-title">
          {{ loginLabel }} is already signed in somewhere else
        </h2>
        <p>Another session has this login in use.</p>

        <template v-if="!resolution">
          <p class="font-medium pt-2">{{ questionText }}</p>
          <div class="flex gap-2">
            <m-button size="sm" theme="primary" @click="answer(true)">Yes</m-button>
            <m-button size="sm" theme="ghost" @click="answer(false)">No</m-button>
          </div>
        </template>

        <template v-else>
          <p class="pt-2">{{ resolutionText }}</p>
          <p v-if="holderStatusText" class="pt-2 font-medium" :class="holderStatusClass">
            {{ holderStatusText }}
          </p>
          <!-- Explains a refused click; drops away once the live line says it's clear. -->
          <p v-if="holderActive && holderStatus !== 'clear'" class="pt-2 font-medium text-warning">
            That didn&#39;t go through — someone made changes in the other session just
            recently, so it&#39;s still in use.
          </p>
          <div class="card-actions justify-between items-center pt-2">
            <m-button theme="link" size="sm" @click="resetQuestions">Start the questions over</m-button>
            <m-button
              theme="primary"
              :loading="claimLoading"
              :disabled="!canContinue"
              @click="tryTakeBack"
              >Continue here</m-button
            >
          </div>
        </template>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useAdminSessionStore } from '@shared/stores/adminSession.store';
  import { useAccountStore } from '@core/stores/account.store';

  const adminSessionStore = useAdminSessionStore();
  const accountStore = useAccountStore();
  const {
    status,
    claimLoading,
    holderActive,
    holderSecondsSinceWrite,
    holderQuietWindowSeconds,
    holderCheckedAt,
  } = storeToRefs(adminSessionStore);

  const visible = computed(() => status.value === 'blocked' || status.value === 'kicked');

  const loginLabel = computed(() => accountStore.email || accountStore.name || 'This login');

  // A short set of questions instead of a paragraph of reasons: the reader has to
  // actually think about WHO is in the other window before the take-over button even
  // appears, and each answer gets only the guidance that applies to them. The questions
  // carry the "are you sure the other window is done?" weight; the server's quiet
  // window separately protects a holder who is actively making changes.
  type Resolution = 'shared' | 'multiComputer' | 'tabs';
  const questionStep = ref<'shared' | 'multiComputer'>('shared');
  const resolution = ref<Resolution | null>(null);

  const questionText = computed(() =>
    questionStep.value === 'shared'
      ? 'Is more than one person using this login?'
      : 'Is it just you, using this login on another computer too?',
  );

  const answer = (yes: boolean) => {
    if (questionStep.value === 'shared') {
      if (yes) resolution.value = 'shared';
      else questionStep.value = 'multiComputer';
    } else {
      resolution.value = yes ? 'multiComputer' : 'tabs';
    }
  };

  const resetQuestions = () => {
    questionStep.value = 'shared';
    resolution.value = null;
  };

  // Live status line: while the modal is open, re-read the holder's write activity every
  // 30s and age the snapshot locally in between (5s ticker), so the reader can watch the
  // other session go from "active" to "quiet" to "you can continue" without clicking.
  // Purely advisory — the take-over click re-checks the server at that moment, so a stale
  // line can never bump an active holder. Polling stops the instant the modal closes, and
  // it only ever runs on a BLOCKED tab reading a skip-guard endpoint, so it can't touch
  // the lease (this is not the holder-traffic polling that poisoned the old design).
  const POLL_INTERVAL_MS = 30_000;
  const TICK_INTERVAL_MS = 5_000;

  const nowTick = ref(Date.now());
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let tickTimer: ReturnType<typeof setInterval> | null = null;

  const refreshActivity = () => {
    void adminSessionStore.checkHolderActivity();
  };

  // Timers freeze while the machine sleeps or the browser parks a hidden tab — re-sync
  // the moment the user comes back so the line never shows a pre-sleep snapshot as fact.
  const onFocus = () => {
    nowTick.value = Date.now();
    refreshActivity();
  };

  const stopWatching = () => {
    if (pollTimer) clearInterval(pollTimer);
    if (tickTimer) clearInterval(tickTimer);
    pollTimer = null;
    tickTimer = null;
    window.removeEventListener('focus', onFocus);
  };

  // The dialog element stays rendered (closed = hidden by the browser); open/close is
  // imperative because that's the only way to get showModal()'s top-layer treatment.
  const dialogRef = ref<HTMLDialogElement | null>(null);
  const syncDialog = (isVisible: boolean) => {
    const dialog = dialogRef.value;
    if (!dialog) return;
    if (isVisible && !dialog.open) dialog.showModal();
    else if (!isVisible && dialog.open) dialog.close();
  };

  // Fresh questions each time the modal appears — the answer can be different next time.
  watch(visible, (isVisible) => {
    syncDialog(isVisible);
    if (!isVisible) {
      resetQuestions();
      stopWatching();
      return;
    }
    nowTick.value = Date.now();
    refreshActivity();
    pollTimer = setInterval(refreshActivity, POLL_INTERVAL_MS);
    tickTimer = setInterval(() => (nowTick.value = Date.now()), TICK_INTERVAL_MS);
    window.addEventListener('focus', onFocus);
  });

  // A conflict can already be showing when this mounts (the interceptor's onConflict can
  // beat the router guard); the watch above misses that because the ref is set at mount.
  onMounted(() => syncDialog(visible.value));

  onBeforeUnmount(stopWatching);

  const agedSecondsSinceWrite = computed(() => {
    if (holderCheckedAt.value === null || holderSecondsSinceWrite.value === null) return null;
    return holderSecondsSinceWrite.value + (nowTick.value - holderCheckedAt.value) / 1000;
  });

  const quietWindowSeconds = computed(() => holderQuietWindowSeconds.value ?? 300);
  const quietWindowMinutes = computed(() => Math.round(quietWindowSeconds.value / 60));

  type HolderStatus = 'active' | 'windingDown' | 'clear';
  const holderStatus = computed<HolderStatus | null>(() => {
    if (holderCheckedAt.value === null) return null; // no successful read yet — say nothing
    const aged = agedSecondsSinceWrite.value;
    if (aged === null || aged >= quietWindowSeconds.value) return 'clear';
    return aged < 60 ? 'active' : 'windingDown';
  });

  const holderStatusText = computed(() => {
    switch (holderStatus.value) {
      case 'active':
        return 'Someone is working in the other session right now — the last change was under a minute ago.';
      case 'windingDown':
        return "The other session has gone quiet — waiting a little longer to be sure it's done.";
      case 'clear':
        // "at least": the quiet stretch could be hours — the snapshot only proves the
        // window has fully elapsed, so the line must not read as an exact measurement.
        return holderSecondsSinceWrite.value === null
          ? "The other session hasn't made any changes — you can continue here now."
          : `No changes in the other session for at least ${quietWindowMinutes.value} minutes — you can continue here now.`;
      default:
        return '';
    }
  });

  // The button unlocks when the other session has been quiet for the whole window (green
  // line) — and also when we have no reading at all (null): the activity check failing
  // must never strand the login behind a dead button. The click re-validates server-side
  // either way, so an enabled button is never a promise the takeover will be accepted.
  const canContinue = computed(
    () => holderStatus.value === 'clear' || holderStatus.value === null,
  );

  const holderStatusClass = computed(() => {
    switch (holderStatus.value) {
      case 'active':
        return 'text-warning';
      case 'clear':
        return 'text-success';
      default:
        return '';
    }
  });

  const resolutionText = computed(() => {
    switch (resolution.value) {
      case 'shared':
        return (
          'Each person needs their own user — you can set those up in Studio Settings under User Settings. Then ' +
          'you can all work at the same time instead of bumping each other out. For now, you can still continue ' +
          'from here — just be sure the other person is not in the middle of something, because whatever they ' +
          'were doing will be cut off where it stands.'
        );
      case 'multiComputer':
        return (
          "That's fine — your other computer is holding the spot. You can continue from here; just know that " +
          'anything left half-finished over there will be cut off where it stands.'
        );
      default:
        return (
          'Then either another tab or window still has the studio open — maybe minimized, or on another screen — ' +
          'or it closed without signing out (a crash, or the browser was shut down). If you can find an open ' +
          "window, use that one. If it's gone, nothing is being interrupted — just continue here."
        );
    }
  });

  const tryTakeBack = () => adminSessionStore.tryTakeBack();
</script>

<style scoped>
  /* Match the old overlay look (bg-base-300/70 + blur) on the native ::backdrop. */
  dialog::backdrop {
    background: hsl(var(--b3) / 0.7);
    backdrop-filter: blur(4px);
  }
</style>
